import React, {createRef} from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import ChatEventCodes from '../utils/ChatEventCodes';

/**
 * Class to handle the base message template interaction.
 */
export default class Message extends React.Component {

    constructor(props) {
        super(props);

        this.messageRef = createRef();
    }

    /**
     * Takes the message time from chat-bot.jsx and creates the time sent for the message bubbles
     * in the chat window.
     */
    buildSendTime() {
        const sendMessageTime = new Date(this.props.message.messageTime);
        return new Intl.DateTimeFormat('default', {
            hour12: true,
            hour: 'numeric',
            minute: 'numeric'
        }).format(sendMessageTime);
    }

    /**
     * Generates the sender text for the new message top line.
     */
    createUpdateMessage() {
        let sender;
        if (this.props.message.agentChat) {
            sender = this.props.payload.agent_name;
        }
        else if (this.props.message.messageSender === 'bot') {
            sender = 'Virtual Assistant';
        }
        else {
            // Do nothing
        }

        return `New message from ${sender} at ${this.buildSendTime()}`;
    }

    componentDidMount() {
        this.handleUserFocus();
    }

    /**
     * Takes the string to be run through markdown, sanitizes it, and then returns
     * the string with markdown HTML replacements.
     * @param {String} value
     */
    evaluateMarkdown(value) {
        marked.use({
            breaks: true
        });
        return {__html: DOMPurify.sanitize(marked.parse(this.stripHtml(value)))};
    }

    /**
     * Generates the sender message for the current chat bubble. If bot sends message,
     * then display the bot text with time. If user responds, display the user text
     * with time. If messages stack, then hide the user send and time message completely.
     */
    generateSenderMessage() {
        let visuallyHiddenClass = '';
        if (this.props.message.messageSender === this.props.message.lastMessageSender) {
            visuallyHiddenClass = 'vh';
        }

        const sendMessageTimeBlock = <span className='sender-message__time'>
            {` at ${this.buildSendTime()}`}
        </span>
        if (this.props.message.agentChat) {
            return (
                <div className={`sender-message sender-message--bot ${visuallyHiddenClass}`} tabIndex='0' ref={this.messageRef}>
                    <span className='sender-message__icon'></span>
                    <span className='sender-message__sender'>{this.props.payload.agent_name}</span>
                    {sendMessageTimeBlock}
                </div>
            )
        }
        else if (this.props.message.messageSender === 'bot') {
            return (
                <div className={`sender-message sender-message--bot ${visuallyHiddenClass}`} tabIndex='0' ref={this.messageRef}>
                    <span className='sender-message__icon'></span>
                    <span className='sender-message__sender'>Virtual Assistant</span>
                    {sendMessageTimeBlock}
                </div>
            )
        }
        return (
            <div className={`sender-message sender-message--user ${visuallyHiddenClass}`} tabIndex='0' ref={this.messageRef}>
                <span className='sender-message__sender'>You</span>
                {sendMessageTimeBlock}
            </div>
        );
    }

    /**
     * Set the CSS class names for the messages based on if they are user or bot generated.
     */
    generateSenderClassName(templateClassName) {
        return this.props.message.messageSender === 'user' ? `message message--user ${templateClassName}` : `message message--bot ${templateClassName}`;
    }

    /**
     * Change the user focus based on if a message is interactable or not. If it is not, then dispatch
     * an event for chat-bot.jsx to update the global aria-live region. If it is, then focus the user
     * on the latest sender message heading.
     */
    handleUserFocus() {
        if (this.props.message.messageSender === 'user' || (this.props.message.messageSender === this.props.message.lastMessageSender)) {
            return;
        }
        if (!this.props.isFocusable || this.props.isFocusable === 'false' || !this.messageRef.current) {
            const data = {
                updateMessage: this.createUpdateMessage()
            };
            document.dispatchEvent(new MessageEvent(ChatEventCodes.UpdateNewMessage, {data: data}));
            return;
        }

        this.messageRef.current.focus();
    }

    /**
     * Strip potential HTML that may be passed from the bot response. If it is HTML,
     * then return an empty string over potentitaly injected data.
     */
    stripHtml(value) {
        const parsedHtml = new DOMParser().parseFromString(value, 'text/html');
        return parsedHtml.body.textContent || '';
    }
}